import type { RoadTripCostBreakdown } from '../types/roadTrip';

interface RoadTripCostBreakdownProps {
  breakdown: RoadTripCostBreakdown;
  tripDays: number;
}

export default function RoadTripCostBreakdown({ breakdown, tripDays }: RoadTripCostBreakdownProps) {
  if (!breakdown) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-4">Cost Breakdown</h2>
        <p className="text-gray-500 text-center py-4">Add stops to calculate costs</p>
      </div>
    );
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: breakdown.currency
    }).format(amount);
  };

  const categories = [
    { key: 'fuel', label: '⛽ Fuel', value: breakdown.fuel, color: 'bg-amber-500' },
    { key: 'tolls', label: '🛣️ Tolls', value: breakdown.tolls, color: 'bg-gray-500' },
    { key: 'accommodation', label: '🏨 Accommodation', value: breakdown.accommodation, color: 'bg-blue-500' },
    { key: 'food', label: '🍽️ Food', value: breakdown.food, color: 'bg-green-500' },
    { key: 'attractions', label: '🎟️ Attractions', value: breakdown.attractions, color: 'bg-purple-500' },
    { key: 'maintenance', label: '🔧 Maintenance', value: breakdown.maintenance, color: 'bg-red-500' }
  ];

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-lg font-semibold mb-4">Cost Breakdown</h2>

      {/* Total */}
      <div className="text-center mb-6 pb-4 border-b">
        <p className="text-sm text-gray-500 mb-1">Estimated Total</p>
        <p className="text-3xl font-bold text-primary-600">
          {formatCurrency(breakdown.total)}
        </p>
        <p className="text-sm text-gray-500">
          {formatCurrency(breakdown.dailyAverage)}/day ({tripDays} days)
        </p>
      </div>

      {/* Breakdown */}
      <div className="space-y-3">
        {categories.map(cat => (
          <div key={cat.key} className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded-full ${cat.color}`} />
              <span className="text-sm text-gray-700">{cat.label}</span>
            </div>
            <span className="font-medium">{formatCurrency(cat.value)}</span>
          </div>
        ))}
      </div>

      {/* Tips */}
      <div className="mt-6 p-4 bg-blue-50 rounded-lg">
        <h3 className="font-medium text-blue-900 mb-2">💡 Cost-Saving Tips</h3>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• Book accommodation in advance for better rates</li>
          <li>• Use gas price apps to find cheapest fuel</li>
          <li>• Consider overnight parking to save on fees</li>
          <li>• Look for free attractions along your route</li>
        </ul>
      </div>
    </div>
  );
}
